import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AppLayout } from "@/components/intel/Layout";
import { exportCaseDossier } from "@/lib/intel/pdf";
import { useInvestigations, useMounted, type Case, type Evidence, type CustodyAction } from "@/lib/intel/store";
import {
  Plus, Search, FolderOpen, ShieldCheck, ShieldAlert, Tag, FileText, Link2,
  Image as ImageIcon, Hash, Trash2, Pin, PinOff, Download, Lock, CheckCircle2,
  AlertTriangle, Clock, FileDigit, X, ChevronRight, Filter, Activity,
} from "lucide-react";

export const Route = createFileRoute("/investigate")({
  head: () => ({
    meta: [
      { title: "Case Workspace · CARIO INTEL" },
      { name: "description", content: "Case files, evidence tagging, analyst notes, and chain-of-custody for active investigations." },
    ],
  }),
  component: InvestigationsPage,
});

function InvestigationsPage() {
  const mounted = useMounted();
  const { state, createCase } = useInvestigations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);

  const cases = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? state.cases.filter((c) =>
          [c.id, c.title, c.target, c.tags.join(" ")].join(" ").toLowerCase().includes(q),
        )
      : state.cases;
    return [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [state.cases, query]);

  const active = selectedId
    ? state.cases.find((c) => c.id === selectedId) ?? null
    : cases[0] ?? null;

  return (
    <AppLayout>
      <div className="grid grid-cols-12 gap-3 p-3">
        {/* Case list */}
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3 glass rounded-xl p-3 flex flex-col min-h-[80vh]">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Case Files</div>
            <button
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 ring-glow">
              <Plus className="h-3 w-3" /> New
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-md border border-panel-border bg-background/40 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cases · target · tag"
              className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="mt-3 -mx-1 flex-1 overflow-y-auto pr-1 space-y-1.5">
            {!mounted && <div className="px-2 py-6 text-center font-mono text-[10px] text-muted-foreground">LOADING CASE INDEX…</div>}
            {mounted && cases.length === 0 && (
              <div className="rounded-md border border-dashed border-panel-border p-6 text-center">
                <FolderOpen className="mx-auto h-5 w-5 text-muted-foreground" />
                <div className="mt-2 text-xs text-muted-foreground">No cases. Create one to begin.</div>
              </div>
            )}
            {mounted && cases.map((c) => {
              const isActive = active?.id === c.id;
              const evCount = state.evidence.filter((e) => e.caseId === c.id).length;
              const noteCount = state.notes.filter((n) => n.caseId === c.id).length;
              return (
                <button key={c.id} onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left rounded-md border px-2.5 py-2 transition-colors
                    ${isActive ? "border-cyan/40 bg-cyan/10" : "border-panel-border bg-background/40 hover:bg-panel/60"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] text-cyan">{c.id}</div>
                    <ClassificationChip c={c.classification} />
                  </div>
                  <div className="mt-1 truncate text-sm font-medium">{c.title}</div>
                  <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">→ {c.target}</div>
                  <div className="mt-1.5 flex items-center gap-3 font-mono text-[10px] text-muted-foreground">
                    <span><FileDigit className="inline h-3 w-3 mr-0.5" />{evCount}</span>
                    <span><FileText className="inline h-3 w-3 mr-0.5" />{noteCount}</span>
                    <StatusDot s={c.status} />
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Case detail */}
        <section className="col-span-12 lg:col-span-8 xl:col-span-9 min-h-[80vh]">
          {active ? <CaseDetail key={active.id} caseRow={active} /> :
            <div className="glass rounded-xl p-10 text-center text-muted-foreground">Select or create a case.</div>}
        </section>
      </div>

      {showNew && (
        <NewCaseModal onClose={() => setShowNew(false)} onCreate={(input) => {
          const id = createCase(input);
          setSelectedId(id);
          setShowNew(false);
        }} />
      )}
    </AppLayout>
  );
}

function ClassificationChip({ c }: { c: Case["classification"] }) {
  const tone =
    c === "SECRET" ? "text-danger ring-danger/40 bg-danger/10" :
    c === "CONFIDENTIAL" ? "text-amber ring-amber/40 bg-amber/10" :
    c === "RESTRICTED" ? "text-cyan ring-cyan/40 bg-cyan/10" :
    "text-muted-foreground ring-panel-border bg-background/40";
  return <span className={`rounded-sm px-1.5 py-0.5 font-mono text-[9px] tracking-widest ring-1 ${tone}`}>{c}</span>;
}

function StatusDot({ s }: { s: Case["status"] }) {
  const map: Record<Case["status"], [string, string]> = {
    active: ["bg-success", "ACTIVE"],
    pending_review: ["bg-amber", "REVIEW"],
    closed: ["bg-muted-foreground", "CLOSED"],
    archived: ["bg-panel-border", "ARCHIVED"],
  };
  const [bg, label] = map[s];
  return <span className="ml-auto inline-flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${bg}`} />{label}</span>;
}

// ----------------- Case detail with tabs -----------------
function CaseDetail({ caseRow }: { caseRow: Case }) {
  const { state, updateCase, verifyChain, logExport } = useInvestigations();
  const [tab, setTab] = useState<"evidence" | "notes" | "custody" | "summary">("evidence");

  const evidence = state.evidence.filter((e) => e.caseId === caseRow.id);
  const notes = state.notes.filter((n) => n.caseId === caseRow.id);
  const custody = state.custody.filter((c) => c.caseId === caseRow.id);
  const chain = verifyChain(caseRow.id);

  return (
    <div id={`case-dossier-${caseRow.id}`} className="glass rounded-xl p-5 flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-4">
        <div className="grid h-14 w-14 place-items-center rounded-xl bg-gradient-to-br from-cyan/30 to-[oklch(0.55_0.20_240)]/30 ring-1 ring-cyan/40">
          <FolderOpen className="h-6 w-6 text-cyan" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-cyan">
            {caseRow.id} <ClassificationChip c={caseRow.classification} />
            <span className="text-muted-foreground">· UPDATED {fmt(caseRow.updatedAt)}</span>
          </div>
          <h1 className="mt-1 font-display text-2xl">{caseRow.title}</h1>
          <div className="mt-1 font-mono text-xs text-muted-foreground">TARGET → {caseRow.target}</div>
          {caseRow.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {caseRow.tags.map((t) => (
                <span key={t} className="rounded-sm border border-panel-border bg-background/40 px-2 py-0.5 font-mono text-[10px] text-muted-foreground">#{t}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ChainBadge ok={chain.valid} brokenAt={chain.brokenAt} />
          <select
            value={caseRow.status}
            onChange={(e) => updateCase(caseRow.id, { status: e.target.value as Case["status"] })}
            className="rounded-md border border-panel-border bg-background/40 px-2 py-1.5 text-xs">
            <option value="active">Active</option>
            <option value="pending_review">Pending review</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
          </select>
          <button
            onClick={async () => {
              const ok = await exportCaseDossier({
                caseRow, evidence, notes, custody, chain,
                filename: `${caseRow.id}-case-dossier.pdf`,
              });
              if (ok) logExport(caseRow.id, `Dossier export · ${evidence.length} evidence · ${notes.length} notes`);
              else alert("PDF export failed — see console for details.");
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs hover:bg-panel">
            <Download className="h-3.5 w-3.5" /> Export dossier
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-panel-border">
        {(
          [
            ["evidence", `Evidence · ${evidence.length}`],
            ["notes",    `Analyst Notes · ${notes.length}`],
            ["custody",  `Chain of Custody · ${custody.length}`],
            ["summary",  "Case Summary"],
          ] as const
        ).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`relative px-3 py-2 text-sm transition-colors ${tab === k ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {l}
            {tab === k && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-cyan" />}
          </button>
        ))}
      </div>

      {tab === "evidence" && <EvidencePanel caseId={caseRow.id} evidence={evidence} />}
      {tab === "notes" && <NotesPanel caseId={caseRow.id} notes={notes} />}
      {tab === "custody" && <CustodyPanel custody={custody} chain={chain} />}
      {tab === "summary" && <SummaryPanel caseRow={caseRow} />}
    </div>
  );
}

function ChainBadge({ ok, brokenAt }: { ok: boolean; brokenAt?: string }) {
  if (ok) return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-success/10 px-2 py-1 font-mono text-[10px] tracking-widest text-success ring-1 ring-success/30">
      <ShieldCheck className="h-3.5 w-3.5" /> CHAIN VERIFIED
    </span>
  );
  return (
    <span title={`Broken at ${brokenAt}`} className="inline-flex items-center gap-1.5 rounded-md bg-danger/10 px-2 py-1 font-mono text-[10px] tracking-widest text-danger ring-1 ring-danger/30">
      <ShieldAlert className="h-3.5 w-3.5" /> CHAIN BROKEN
    </span>
  );
}

// ----------------- Evidence -----------------
function EvidencePanel({ caseId, evidence }: { caseId: string; evidence: Evidence[] }) {
  const { addEvidence, tagEvidence, deleteEvidence } = useInvestigations();
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState("");

  const filtered = filter.trim()
    ? evidence.filter((e) => [e.label, e.source, e.content, e.tags.join(" ")].join(" ").toLowerCase().includes(filter.toLowerCase()))
    : evidence;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-panel-border bg-background/40 px-2 py-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter evidence by tag, source, content…" className="flex-1 bg-transparent text-xs outline-none" />
        </div>
        <button onClick={() => setAdding((v) => !v)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 ring-glow">
          <Plus className="h-3.5 w-3.5" /> {adding ? "Cancel" : "Add evidence"}
        </button>
      </div>

      {adding && <AddEvidenceForm caseId={caseId} onDone={() => setAdding(false)} onSubmit={(input) => { addEvidence(caseId, input); setAdding(false); }} />}

      {filtered.length === 0 && !adding && (
        <div className="rounded-md border border-dashed border-panel-border p-8 text-center text-sm text-muted-foreground">
          No evidence yet. Click <span className="text-foreground">Add evidence</span> to record the first artifact.
        </div>
      )}

      <div className="grid gap-2">
        {filtered.map((e) => (
          <EvidenceRow key={e.id} ev={e} onTag={(tags) => tagEvidence(e.id, tags)} onDelete={() => deleteEvidence(e.id)} />
        ))}
      </div>
    </div>
  );
}

function EvidenceRow({ ev, onTag, onDelete }: { ev: Evidence; onTag: (t: string[]) => void; onDelete: () => void }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const Icon = ev.kind === "url" ? Link2 :
               ev.kind === "screenshot" ? ImageIcon :
               ev.kind === "ioc" ? Hash :
               ev.kind === "transcript" ? FileText :
               ev.kind === "doc" ? FileText : FileDigit;

  const submit = () => {
    const v = draft.trim().replace(/^#/, "");
    if (!v) { setAdding(false); return; }
    if (!ev.tags.includes(v)) onTag([...ev.tags, v]);
    setDraft(""); setAdding(false);
  };

  return (
    <div className="rounded-md border border-panel-border bg-background/40 p-3">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
          <Icon className="h-4 w-4 text-cyan" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] tracking-widest text-cyan">{ev.id}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{ev.kind}</span>
            <span className="font-mono text-[10px] text-muted-foreground">· {ev.source}</span>
            <span className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground" title="SHA-style fingerprint">
              <Lock className="h-3 w-3" /> {ev.hash}
            </span>
          </div>
          <div className="mt-1 text-sm font-medium">{ev.label}</div>
          <div className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">{ev.content}</div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {ev.tags.map((t) => (
              <span key={t} className="group inline-flex items-center gap-1 rounded-sm border border-panel-border bg-panel/50 px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                #{t}
                <button onClick={() => onTag(ev.tags.filter((x) => x !== t))} className="text-muted-foreground/60 hover:text-danger" aria-label={`Remove tag ${t}`}>
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
            {adding ? (
              <span className="inline-flex items-center gap-1 rounded-sm border border-cyan/40 bg-cyan/10 px-1 py-0.5">
                <Tag className="h-3 w-3 text-cyan" />
                <input
                  autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") { setAdding(false); setDraft(""); } }}
                  onBlur={submit}
                  placeholder="tag"
                  className="w-20 bg-transparent text-[11px] outline-none placeholder:text-muted-foreground" />
              </span>
            ) : (
              <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded-sm border border-dashed border-panel-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:text-foreground hover:border-cyan/50">
                <Plus className="h-2.5 w-2.5" /> tag
              </button>
            )}
            <span className="ml-auto font-mono text-[10px] text-muted-foreground">collected {fmt(ev.collectedAt)} · {ev.collectedBy}</span>
            <button onClick={onDelete} className="text-muted-foreground/60 hover:text-danger" aria-label="Delete evidence">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddEvidenceForm({ onSubmit }: { caseId: string; onDone: () => void; onSubmit: (i: { kind: Evidence["kind"]; label: string; source: string; content: string; tags: string[] }) => void }) {
  const [kind, setKind] = useState<Evidence["kind"]>("url");
  const [label, setLabel] = useState("");
  const [source, setSource] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!label.trim() || !content.trim()) return;
      onSubmit({ kind, label: label.trim(), source: source.trim() || "manual", content: content.trim(),
        tags: tags.split(/[,\s]+/).map((t) => t.replace(/^#/, "").trim()).filter(Boolean) });
      setLabel(""); setSource(""); setContent(""); setTags("");
    }} className="rounded-md border border-cyan/30 bg-cyan/5 p-3 space-y-2">
      <div className="grid grid-cols-12 gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as Evidence["kind"])}
          className="col-span-3 rounded-md border border-panel-border bg-background/60 px-2 py-1.5 text-xs">
          {(["url", "ioc", "screenshot", "transcript", "doc", "file"] as const).map((k) => <option key={k} value={k}>{k.toUpperCase()}</option>)}
        </select>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. OpenCorporates filing)"
          className="col-span-5 rounded-md border border-panel-border bg-background/60 px-2 py-1.5 text-xs" required />
        <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source (e.g. opencorporates.com)"
          className="col-span-4 rounded-md border border-panel-border bg-background/60 px-2 py-1.5 text-xs" />
      </div>
      <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="URL / IOC / transcript / file path…" rows={2}
        className="w-full rounded-md border border-panel-border bg-background/60 px-2 py-1.5 text-xs font-mono" required />
      <div className="flex items-center gap-2">
        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags (comma or space separated)"
          className="flex-1 rounded-md border border-panel-border bg-background/60 px-2 py-1.5 text-xs" />
        <button type="submit" className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 ring-glow">
          <CheckCircle2 className="h-3.5 w-3.5" /> Record evidence
        </button>
      </div>
    </form>
  );
}

// ----------------- Notes -----------------
function NotesPanel({ caseId, notes }: { caseId: string; notes: ReturnType<typeof useInvestigations>["state"]["notes"] }) {
  const { addNote, deleteNote, togglePin } = useInvestigations();
  const [body, setBody] = useState("");

  const sorted = [...notes].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned) || b.ts.localeCompare(a.ts));

  return (
    <div className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); if (!body.trim()) return; addNote(caseId, body); setBody(""); }}
        className="rounded-md border border-panel-border bg-background/40 p-2">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Analyst note. Hypotheses, next steps, contradictions…"
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
        <div className="mt-1 flex items-center gap-2">
          <span className="font-mono text-[10px] text-muted-foreground">⌘+Enter to file · attaches to chain-of-custody</span>
          <button type="submit" disabled={!body.trim()} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
            <Plus className="h-3.5 w-3.5" /> File note
          </button>
        </div>
      </form>

      {sorted.length === 0 && (
        <div className="rounded-md border border-dashed border-panel-border p-8 text-center text-sm text-muted-foreground">No analyst notes yet.</div>
      )}
      <div className="grid gap-2">
        {sorted.map((n) => (
          <div key={n.id} className={`rounded-md border p-3 ${n.pinned ? "border-amber/40 bg-amber/5" : "border-panel-border bg-background/40"}`}>
            <div className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
              <span className="text-cyan">{n.id}</span>
              <span>· {n.author}</span>
              <span>· {fmt(n.ts)}</span>
              {n.pinned && <span className="ml-1 text-amber">PINNED</span>}
              <button onClick={() => togglePin(n.id)} className="ml-auto text-muted-foreground hover:text-amber" aria-label="Pin">
                {n.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
              </button>
              <button onClick={() => deleteNote(n.id)} className="text-muted-foreground hover:text-danger" aria-label="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{n.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------------- Custody -----------------
const ACTION_TONE: Record<CustodyAction, string> = {
  case_created:    "text-cyan ring-cyan/30 bg-cyan/10",
  case_updated:    "text-muted-foreground ring-panel-border bg-background/40",
  scan_started:    "text-cyan ring-cyan/30 bg-cyan/10",
  scan_completed:  "text-success ring-success/30 bg-success/10",
  evidence_added:  "text-success ring-success/30 bg-success/10",
  evidence_tagged: "text-cyan ring-cyan/30 bg-cyan/10",
  evidence_deleted:"text-danger ring-danger/30 bg-danger/10",
  note_added:      "text-amber ring-amber/30 bg-amber/10",
  note_deleted:    "text-danger ring-danger/30 bg-danger/10",
  status_changed:  "text-amber ring-amber/30 bg-amber/10",
  exported:        "text-cyan ring-cyan/30 bg-cyan/10",
};

function CustodyPanel({ custody, chain }: { custody: ReturnType<typeof useInvestigations>["state"]["custody"]; chain: { valid: boolean; brokenAt?: string } }) {
  return (
    <div className="space-y-3">
      <div className={`rounded-md border p-3 flex items-center gap-3 ${chain.valid ? "border-success/30 bg-success/5" : "border-danger/40 bg-danger/10"}`}>
        {chain.valid ? <ShieldCheck className="h-5 w-5 text-success" /> : <AlertTriangle className="h-5 w-5 text-danger" />}
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Hash chain integrity</div>
          <div className="text-sm">
            {chain.valid
              ? `All ${custody.length} custody events verify against the previous hash. Tamper-evident.`
              : `Chain broken at ${chain.brokenAt}. Evidence integrity cannot be guaranteed.`}
          </div>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">FNV-1a · 64-bit display fingerprint</span>
      </div>

      {custody.length === 0 && <div className="rounded-md border border-dashed border-panel-border p-8 text-center text-sm text-muted-foreground">No custody events yet.</div>}

      <div className="overflow-hidden rounded-md border border-panel-border">
        <div className="grid grid-cols-12 gap-2 border-b border-panel-border bg-panel/40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <div className="col-span-2">Time (UTC)</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-2">Ref</div>
          <div className="col-span-2">Detail</div>
          <div className="col-span-2">Hash</div>
        </div>
        <div className="divide-y divide-panel-border">
          {[...custody].reverse().map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs">
              <div className="col-span-2 font-mono text-[11px] text-muted-foreground"><Clock className="inline h-3 w-3 mr-1 -mt-0.5" />{fmt(c.ts)}</div>
              <div className="col-span-2">
                <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-[10px] tracking-widest ring-1 ${ACTION_TONE[c.action]}`}>
                  <Activity className="h-3 w-3" /> {c.action.toUpperCase()}
                </span>
              </div>
              <div className="col-span-2 truncate">{c.actor}</div>
              <div className="col-span-2 truncate font-mono text-[11px] text-cyan">{c.ref ?? "—"}</div>
              <div className="col-span-2 truncate text-muted-foreground" title={c.detail}>{c.detail ?? "—"}</div>
              <div className="col-span-2 truncate font-mono text-[11px] text-muted-foreground" title={c.hash}>
                <Lock className="inline h-3 w-3 mr-1" />{c.hash}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ----------------- Summary -----------------
function SummaryPanel({ caseRow }: { caseRow: Case }) {
  const { updateCase } = useInvestigations();
  const [draft, setDraft] = useState(caseRow.summary);
  const dirty = draft !== caseRow.summary;
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="md:col-span-2 rounded-md border border-panel-border bg-background/40 p-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Investigation summary</div>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={10}
          placeholder="Working hypothesis, methodology, findings synthesis…"
          className="mt-2 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
        <div className="mt-2 flex items-center gap-2">
          <span className="font-mono text-[10px] text-muted-foreground">{draft.length} chars</span>
          <button onClick={() => updateCase(caseRow.id, { summary: draft })} disabled={!dirty}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40">
            <CheckCircle2 className="h-3.5 w-3.5" /> Save summary
          </button>
        </div>
      </div>
      <div className="rounded-md border border-panel-border bg-background/40 p-3 space-y-2 text-sm">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Case metadata</div>
        <Meta k="ID" v={caseRow.id} />
        <Meta k="Owner" v={caseRow.owner} />
        <Meta k="Status" v={caseRow.status} />
        <Meta k="Classification" v={caseRow.classification} />
        <Meta k="Created" v={fmt(caseRow.createdAt)} />
        <Meta k="Updated" v={fmt(caseRow.updatedAt)} />
        <Meta k="Target" v={caseRow.target} mono />
      </div>
    </div>
  );
}

function Meta({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-panel-border/50 pb-1.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</span>
      <span className={`text-right truncate ${mono ? "font-mono text-xs" : "text-sm"}`}>{v}</span>
    </div>
  );
}

// ----------------- New case modal -----------------
function NewCaseModal({ onClose, onCreate }: { onClose: () => void; onCreate: (i: { title: string; target: string; classification: Case["classification"]; tags: string[] }) => void }) {
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [classification, setClassification] = useState<Case["classification"]>("RESTRICTED");
  const [tags, setTags] = useState("");

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-background/80 backdrop-blur-sm p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault();
          if (!title.trim() || !target.trim()) return;
          onCreate({ title, target, classification, tags: tags.split(/[,\s]+/).map(t=>t.replace(/^#/,"").trim()).filter(Boolean) });
        }}
        className="glass-strong w-full max-w-lg rounded-2xl p-6 ring-glow">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
          <Plus className="h-3.5 w-3.5" /> Open new investigation
        </div>
        <h2 className="mt-2 font-display text-xl">Case intake</h2>
        <div className="mt-4 space-y-3">
          <Field label="Case title">
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Argus Trading — UBO disclosure" required
              className="w-full rounded-md border border-panel-border bg-background/60 px-3 py-2 text-sm outline-none focus:border-cyan/60" />
          </Field>
          <Field label="Primary target">
            <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="email · domain · wallet · org · person" required
              className="w-full rounded-md border border-panel-border bg-background/60 px-3 py-2 text-sm font-mono outline-none focus:border-cyan/60" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Classification">
              <select value={classification} onChange={(e) => setClassification(e.target.value as Case["classification"])}
                className="w-full rounded-md border border-panel-border bg-background/60 px-3 py-2 text-sm">
                <option>UNCLASSIFIED</option>
                <option>RESTRICTED</option>
                <option>CONFIDENTIAL</option>
                <option>SECRET</option>
              </select>
            </Field>
            <Field label="Tags">
              <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="financial, ubo, crypto"
                className="w-full rounded-md border border-panel-border bg-background/60 px-3 py-2 text-sm" />
            </Field>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-2">
          <span className="font-mono text-[10px] text-muted-foreground">Lawful OSINT collection only · auto-logged to custody chain</span>
          <button type="button" onClick={onClose} className="ml-auto rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs">Cancel</button>
          <button type="submit" className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 ring-glow">
            <ChevronRight className="h-3.5 w-3.5" /> Open case
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  );
}

// ----------------- utils -----------------
function fmt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toISOString().replace("T", " ").slice(0, 16) + "Z";
  } catch { return iso; }
}